import { useEffect, useRef, useState } from "react"

import { ColumnDef, ColumnFiltersState, FilterFn, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, SortingState, useReactTable, VisibilityState } from "@tanstack/react-table"
import { ContentObjectItem } from "@vertesia/common"
import { Minus, Plus, Loader2 } from "lucide-react"

import { ColumnFilter } from "@components/datatables/filters/ColumnFilter"
import { DocumentOverlay } from "@components/overlays/DocumentOverlay"

import { useIntersectionObserver } from "@hooks/use-intersection-observer"

import { Button } from "@ui/button"
import { Skeleton } from "@ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ui/table"

function columns(onSelection?: (document: ContentObjectItem) => void, selections?: ContentObjectItem[]): ColumnDef<ContentObjectItem>[] {
    const nameFilterFn: FilterFn<ContentObjectItem> = (row, filterValue) => {
        const nameValue = row.original?.name?.toLowerCase() ?? ""
        const searchValue = filterValue.toLowerCase()

        return nameValue.includes(searchValue)
    }

    const typeFilterFn: FilterFn<ContentObjectItem> = (row, filterValue) => {
        const typeValue = row.original?.type?.name?.toLowerCase() ?? ""
        const searchValue = filterValue.toLowerCase()

        return typeValue.includes(searchValue)
    }

    const columns: ColumnDef<ContentObjectItem>[] = [
        {
            accessorKey: "name",
            header: ({ column }) => <ColumnFilter column={column} label="Name" hideFilter />,
            filterFn: nameFilterFn,
            sortingFn: (rowA, rowB) => {
                const nameA = rowA.original?.name?.toLowerCase() ?? ""
                const nameB = rowB.original?.name?.toLowerCase() ?? ""
                return nameA.localeCompare(nameB)
            },
            cell: ({ row }) => {
                return (
                    <DocumentOverlay document={row.original.id}>
                        <div className="underline text-blue-500 hover:cursor-pointer">{(row.original as any)?.properties?.title || row.getValue("name") || row.getValue("id")}</div>
                    </DocumentOverlay>
                )
            },
            size: 300,
        },
        {
            accessorKey: "type",
            header: ({ column }) => <ColumnFilter column={column} label="Type" hideFilter />,
            filterFn: typeFilterFn,
            sortingFn: (rowA, rowB) => {
                const orgA = rowA.original?.type?.name?.toLowerCase() ?? ""
                const orgB = rowB.original?.type?.name?.toLowerCase() ?? ""
                return orgA.localeCompare(orgB)
            },
            cell: ({ row }) => {
                return <div>{row.original?.type?.name}</div>
            },
            size: 200,
        },
    ]

    if (onSelection) {
        columns.push({
            id: "actions",
            enableHiding: false,
            cell: ({ row }) => {
                return (
                    <div className="flex items-center justify-center">
                        <Button variant="outline" size={"icon"} onClick={() => onSelection(row.original)}>
                            {selections?.some((item) => item.id === row.original.id) ? <Minus /> : <Plus />}
                        </Button>
                    </div>
                )
            },
            size: 70,
        })
    }

    return columns
}

interface DocumentDataTableProps {
    data: ContentObjectItem[]
    loading: boolean
    loadMore?: () => void
    hasMore?: boolean
    loadingMore?: boolean
    onSelection?: (document: ContentObjectItem) => void
    selections?: ContentObjectItem[]
}
export function DocumentDataTable({ data, loading, loadMore, hasMore = false, loadingMore = false, onSelection, selections }: Readonly<DocumentDataTableProps>) {
    const [sorting, setSorting] = useState<SortingState>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
    const tableContainerRef = useRef<HTMLDivElement>(null)
    const loadMoreRef = useRef<HTMLTableRowElement>(null)

    const table = useReactTable({
        data,
        columns: columns(onSelection, selections),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
        },
        enableMultiSort: true,
        columnResizeMode: "onChange",
    })

    const handleScroll = () => {
        if (!tableContainerRef.current || loading || loadingMore || !hasMore || !loadMore) {
            return
        }

        const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current
        const diff = scrollHeight - scrollTop - clientHeight

        if (diff < 50) {
            loadMore()
        }
    }

    useEffect(() => {
        const tableContainer = tableContainerRef.current
        if (tableContainer) {
            tableContainer.addEventListener("scroll", handleScroll)

            return () => {
                tableContainer.removeEventListener("scroll", handleScroll)
            }
        }
    }, [loading, loadingMore, hasMore])

    useIntersectionObserver(loadMoreRef, () => {
        loadMore?.()
    }, { deps: [data.length] })

    return (
        <div className="flex flex-col h-full w-full">
            <div ref={tableContainerRef} className="flex flex-col h-full border rounded-md border-gray bg-white overflow-auto">
                <Table className="w-full">
                    <TableHeader className="sticky top-0 bg-white z-10 after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-gray-200">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} style={{ maxWidth: header.getSize() }}>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={onSelection ? 3 : 2}>
                                    <div className="flex justify-center items-center w-full h-full">
                                        <div className="w-full h-full space-y-2">
                                            {[...Array(50)].map(() => (
                                                <Skeleton key={crypto.randomUUID()} className="h-12 w-full" />
                                            ))}
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading &&
                            table.getRowModel().rows?.length > 0 &&
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} style={{ width: cell.column.getSize() }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        {!loading && table.getRowModel().rows?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={onSelection ? 3 : 2}>
                                    <div className="flex justify-center items-center w-full">
                                        <p className="text-gray-500 text-center">No documents found.</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {loadingMore && (
                            <TableRow>
                                <TableCell colSpan={onSelection ? 3 : 2}>
                                    <div className="flex justify-center items-center p-4">
                                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {hasMore && loadMore && !loadingMore && data.length > 0 && (
                            <TableRow ref={loadMoreRef}>
                                <TableCell colSpan={onSelection ? 3 : 2} className="h-1 p-0" />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {data.length != 0 && hasMore && data.length < 10 && <div style={{ minHeight: "100px" }}></div>}
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2 text-sm text-muted-foreground">
                {loading ? (
                    loadingMore ? (
                        " Loading more..."
                    ) : (
                        "Loading documents..."
                    )
                ) : data.length === 0 ? (
                    "No results."
                ) : (
                    <>
                        {table.getFilteredRowModel().rows.length} document{table.getFilteredRowModel().rows.length <= 1 ? "" : "s"}.
                    </>
                )}
            </div>
        </div>
    )
}
